import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGERIMENTI_INIZIALI = [
  'Quanto devo pagare?',
  'Quali quote sono scadute?',
  'Quando ho pagato l\'ultima volta?',
  'Dove trovo gli ultimi avvisi?',
  'Come segnalo un pagamento?',
  'Come segnalo un problema?',
];

const BENVENUTO = {
  da: 'assistente',
  testo: 'Ciao! Sono l\'assistente di Via Libertà 193. Ti aiuto a capire a che punto sono le tue quote, quando e come hai pagato, dove trovare un avviso e come scrivere all\'amministratore. Rispondo solo sui tuoi dati. Cosa ti serve?',
  azioni: [],
  suggerimenti: SUGGERIMENTI_INIZIALI,
};

// Assistente del condomino: pannello in basso a destra, solo per il ruolo condomino.
// Risponde con le regole e i dati del server (nessun servizio esterno); non registra e non invia nulla.
export const AssistenteCondomino = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const ruolo = selectedCondominium?.role;
  const [aperto, setAperto] = useState(false);
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [messaggi, setMessaggi] = useState([BENVENUTO]);
  const fondo = useRef(null);
  const campo = useRef(null);

  useEffect(() => {
    if (aperto && fondo.current) fondo.current.scrollIntoView({ block: 'end' });
  }, [messaggi, aperto]);

  useEffect(() => {
    if (aperto && campo.current) campo.current.focus();
  }, [aperto]);

  if (ruolo !== 'RESIDENT' || !condoId) return null;

  const chiedi = async (domanda) => {
    const d = (domanda || '').trim();
    if (!d || invio) return;
    setTesto('');
    setInvio(true);
    setMessaggi((m) => [...m, { da: 'utente', testo: d }]);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/assistente/chiedi`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ domanda: d }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        const motivo = typeof j.detail === 'string' ? j.detail : 'Non riesco a rispondere in questo momento. Riprova tra poco.';
        setMessaggi((m) => [...m, { da: 'assistente', testo: motivo, azioni: [], suggerimenti: [] }]);
      } else {
        setMessaggi((m) => [...m, { da: 'assistente', testo: j.testo, azioni: j.azioni || [], suggerimenti: j.suggerimenti || [] }]);
      }
    } catch (e) {
      setMessaggi((m) => [...m, { da: 'assistente', testo: 'Non riesco a collegarmi. Controlla la connessione e riprova.', azioni: [], suggerimenti: [] }]);
    }
    setInvio(false);
  };

  const ultimo = messaggi.length - 1;

  return (
    <>
      {!aperto && (
        <button
          type="button"
          onClick={() => setAperto(true)}
          aria-label="Apri l'assistente"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg hover:opacity-90"
        >
          <MessageCircle className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm font-medium">Assistente</span>
        </button>
      )}

      {aperto && (
        <div
          role="dialog"
          aria-label="Assistente di Via Libertà 193"
          className="fixed bottom-4 right-4 z-50 flex w-[min(94vw,390px)] flex-col overflow-hidden rounded-xl border bg-background shadow-2xl"
          style={{ height: 'min(80vh, 580px)' }}
        >
          <div className="flex items-center justify-between bg-primary px-4 py-3 text-primary-foreground">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              <span className="text-sm font-semibold">Assistente</span>
            </div>
            <button type="button" onClick={() => setAperto(false)} aria-label="Chiudi l'assistente" className="rounded p-1 hover:bg-white/20">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-3" aria-live="polite">
            {messaggi.map((m, i) => (
              <div key={i} className={m.da === 'utente' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`max-w-[88%] rounded-lg px-3 py-2 text-sm ${m.da === 'utente' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <p className="whitespace-pre-line">{m.testo}</p>
                  {m.da === 'assistente' && (m.azioni || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.azioni.map((a) => (
                        <Link
                          key={a.percorso + a.etichetta}
                          to={a.percorso}
                          onClick={() => setAperto(false)}
                          className="rounded-md border border-primary bg-background px-2 py-1 text-xs font-medium text-primary hover:bg-primary hover:text-primary-foreground"
                        >
                          {a.etichetta}
                        </Link>
                      ))}
                    </div>
                  )}
                  {i === ultimo && m.da === 'assistente' && (m.suggerimenti || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.suggerimenti.map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={invio}
                          onClick={() => chiedi(s)}
                          className="rounded-full border bg-background px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {invio && <div className="text-xs text-muted-foreground">Sto cercando...</div>}
            <div ref={fondo} />
          </div>

          <form
            className="flex items-center gap-2 border-t p-2"
            onSubmit={(e) => { e.preventDefault(); chiedi(testo); }}
          >
            <input
              ref={campo}
              value={testo}
              maxLength={400}
              onChange={(e) => setTesto(e.target.value)}
              placeholder="Scrivi la tua domanda"
              aria-label="Scrivi la tua domanda"
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={invio || !testo.trim()}
              aria-label="Invia la domanda"
              className="rounded-md bg-primary p-2 text-primary-foreground disabled:opacity-50"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
          <p className="px-3 pb-2 text-[11px] leading-snug text-muted-foreground">
            Rispondo solo con i dati registrati nell'app e solo sui tuoi. Non registro pagamenti e non invio nulla. Per tutto il resto scrivi all'amministratore.
          </p>
        </div>
      )}
    </>
  );
};

export default AssistenteCondomino;
